import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import netlifyIdentity from "netlify-identity-widget";
import TurndownService from "turndown";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import { contentfulClient } from "../../lib/contentful";
import { Editor } from "@tinymce/tinymce-react";

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const CATEGORY_CONTENT_TYPE =
  process.env.REACT_APP_CONTENTFUL_CATEGORY_TYPE || "category";

const getLocalizedValue = (value, preferredLocales) => {
  if (!value) return value;
  if (value.nodeType || value.sys) return value;
  if (Array.isArray(value)) return value;
  if (typeof value === "object") {
    for (const locale of preferredLocales) {
      if (value[locale]) return value[locale];
    }
    const keys = Object.keys(value);
    if (keys.length) return value[keys[0]];
  }
  return value;
};

const Admin = () => {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const editorRef = useRef(null);
  const richImageInputRef = useRef(null);
  const [form, setForm] = useState({
    title: "",
    slug: "",
    shortDescription: "",
    location: "",
    publishedDate: "",
    contentHtml: "",
    featuredImage: null,
    categories: [],
    publishNow: false,
  });
  const [editorValue, setEditorValue] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [availableCategories, setAvailableCategories] = useState([]);
  const preferredLocales = useMemo(() => {
    const raw =
      process.env.REACT_APP_CONTENTFUL_LOCALES ||
      process.env.REACT_APP_CONTENTFUL_LOCALE ||
      "en-US";
    return raw
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }, []);

  const turndown = useMemo(() => {
    const service = new TurndownService();
    service.addRule("codeBlock", {
      filter: (node) =>
        node.nodeType === 1 &&
        node.nodeName === "PRE" &&
        node.querySelector &&
        node.querySelector("code"),
      replacement: (_content, node) => {
        const codeNode = node.querySelector("code");
        const text = codeNode ? codeNode.textContent || "" : node.textContent || "";
        const className =
          (codeNode && codeNode.getAttribute("class")) ||
          node.getAttribute("class") ||
          "";
        const langMatch = className.match(/language-([a-z0-9-]+)/i);
        const lang = langMatch ? langMatch[1] : "";
        return `\n\n\`\`\`${lang}\n${text}\n\`\`\`\n\n`;
      },
    });
    service.addRule("codeSnippetToken", {
      filter: (node) =>
        node.nodeType === 1 &&
        node.nodeName === "PRE" &&
        node.getAttribute("data-code-snippet-id"),
      replacement: (_content, node) => {
        const id = node.getAttribute("data-code-snippet-id");
        return id ? `[[[CODESNIPPET:${id}]]]` : "";
      },
    });
    service.addRule("richImageToken", {
      filter: (node) =>
        node.nodeType === 1 &&
        node.tagName === "IMG" &&
        node.getAttribute("data-rich-image-id"),
      replacement: (_content, node) => {
        const id = node.getAttribute("data-rich-image-id");
        return id ? `[[[RICHIIMAGE:${id}]]]` : "";
      },
    });
    service.addRule("richImageTokenSpan", {
      filter: (node) =>
        node.nodeType === 1 &&
        node.tagName === "SPAN" &&
        node.getAttribute("data-rich-image-token"),
      replacement: (_content, node) => {
        const id = node.getAttribute("data-rich-image-token");
        return id ? `[[[RICHIIMAGE:${id}]]]` : "";
      },
    });
    return service;
  }, []);

  useEffect(() => {
    netlifyIdentity.init();
    const current = netlifyIdentity.currentUser();
    setUser(current);

    const handleLogin = (loggedInUser) => {
      setUser(loggedInUser);
      netlifyIdentity.close();
    };

    const handleLogout = () => {
      setUser(null);
    };

    netlifyIdentity.on("login", handleLogin);
    netlifyIdentity.on("logout", handleLogout);

    return () => {
      netlifyIdentity.off("login", handleLogin);
      netlifyIdentity.off("logout", handleLogout);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slugParam = params.get("edit");
    if (slugParam) setEditSlug(slugParam);
  }, []);

  useEffect(() => {
    const loadEntry = async () => {
      if (!editSlug || !contentfulClient) return;
      try {
        const clientWithLocales =
          contentfulClient && typeof contentfulClient.withAllLocales === "function"
            ? contentfulClient.withAllLocales()
            : contentfulClient;
        const response = await clientWithLocales.getEntries({
          content_type: "pageBlogPost",
          "fields.slug": editSlug,
          limit: 1,
          include: 2,
        });
        const entry = response.items && response.items[0];
        if (!entry) return;

        const assets =
          response.includes && response.includes.Asset
            ? response.includes.Asset
            : [];
        const assetsById = assets.reduce((acc, asset) => {
          acc[asset.sys.id] = asset;
          return acc;
        }, {});

        const entries =
          response.includes && response.includes.Entry
            ? response.includes.Entry
            : [];
        const entriesById = entries.reduce((acc, item) => {
          acc[item.sys.id] = item;
          return acc;
        }, {});

        const title = getLocalizedValue(entry.fields.title, preferredLocales) || "";
        const slug = getLocalizedValue(entry.fields.slug, preferredLocales) || editSlug;
        const shortDescription =
          getLocalizedValue(entry.fields.shortDescription, preferredLocales) || "";
        const location =
          getLocalizedValue(entry.fields.location, preferredLocales) || "";
        const publishedDate =
          getLocalizedValue(entry.fields.publishedDate, preferredLocales) || "";
        const content = getLocalizedValue(entry.fields.content, preferredLocales);
        const categories = Array.isArray(entry.fields.categories)
          ? entry.fields.categories
          : [];
        const categoryIds = categories
          .map((category) => category?.sys?.id)
          .filter(Boolean);

        const escapeHtml = (value) =>
          value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

        const html = content
          ? documentToHtmlString(content, {
              renderNode: {
                [BLOCKS.EMBEDDED_ASSET]: (node) => {
                  const target =
                    node.data.target?.fields || node.data.target?.sys
                      ? node.data.target
                      : assetsById[node.data.target?.sys?.id];
                  const fileField = target?.fields?.file;
                  const file =
                    fileField && fileField.url
                      ? fileField
                      : getLocalizedValue(fileField) || {};
                  const url = file.url ? `https:${file.url}` : "";
                  const titleText =
                    getLocalizedValue(target?.fields?.title) || "Embedded image";
                  return url
                    ? `<img src="${escapeHtml(url)}" alt="${escapeHtml(
                        titleText
                      )}" />`
                    : "";
                },
                [BLOCKS.EMBEDDED_ENTRY]: (node) => {
                  const target =
                    node.data.target?.fields
                      ? node.data.target
                      : entriesById[node.data.target?.sys?.id];
                  if (!target) return "";
                  if (target.sys?.contentType?.sys?.id !== "componentRichImage") {
                    if (target.sys?.contentType?.sys?.id === "codeSnippet") {
                      const fields = target.fields || {};
                      const language = getLocalizedValue(fields.language) || "";
                      const code = getLocalizedValue(fields.code) || "";
                      return `<pre class="code-snippet-block" data-code-snippet-id="${escapeHtml(
                        target.sys.id
                      )}"><div class="code-snippet-meta">${
                        language ? escapeHtml(language) : "code"
                      }</div><code>${escapeHtml(code)}</code></pre>`;
                    }
                    return "";
                  }
                  const fields = target.fields || {};
                  const imageField = getLocalizedValue(fields.image);
                  const image =
                    imageField && !imageField.fields
                      ? assetsById[imageField.sys?.id]
                      : imageField;
                  const fileField = image?.fields?.file;
                  const file =
                    fileField && fileField.url
                      ? fileField
                      : getLocalizedValue(fileField) || {};
                  const url = file.url ? `https:${file.url}` : "";
                  const caption = getLocalizedValue(fields.caption) || "";
                  const fullWidth = Boolean(getLocalizedValue(fields.fullWidth));
                  if (!url) return "";
                  return `<figure class="blog-rich-image${
                    fullWidth ? " full-width" : ""
                  }"><img src="${escapeHtml(
                    url
                  )}" data-rich-image-id="${escapeHtml(
                    target.sys.id
                  )}" alt="${escapeHtml(
                    caption || "Rich image"
                  )}" />${
                    caption
                      ? `<figcaption>${escapeHtml(caption)}</figcaption>`
                      : ""
                  }</figure>`;
                },
              },
            })
          : "";


        setForm((prev) => ({
          ...prev,
          title,
          slug,
          shortDescription,
          location,
          publishedDate: publishedDate.split("T")[0] || publishedDate,
          categories: categoryIds,
        }));
        setEditorValue(html);
      } catch (error) {
        console.error("Load entry failed:", error);
      }
    };

    loadEntry();
  }, [editSlug, preferredLocales]);

  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      if (!contentfulClient) return;
      try {
        const clientWithLocales =
          contentfulClient && typeof contentfulClient.withAllLocales === "function"
            ? contentfulClient.withAllLocales()
            : contentfulClient;
        const response = await clientWithLocales.getEntries({
          content_type: CATEGORY_CONTENT_TYPE,
          order: "fields.name",
          limit: 200,
        });
        if (!isMounted) return;
        const items = response.items || [];
        const mapped = items
          .map((entry) => ({
            id: entry.sys.id,
            label:
              getLocalizedValue(entry.fields?.name, preferredLocales) ||
              getLocalizedValue(entry.fields?.title, preferredLocales) ||
              getLocalizedValue(entry.fields?.internalName, preferredLocales) ||
              entry.sys.id,
          }))
          .filter((entry) => entry.label);
        setAvailableCategories(mapped);
      } catch (error) {
        console.error("Load categories failed:", error);
      }
    };
    loadCategories();
    return () => {
      isMounted = false;
    };
  }, [preferredLocales]);

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "title" ? { slug: slugify(value) } : null),
    }));
  };

  const toggleCategory = (categoryId) => {
    setForm((prev) => {
      const current = Array.isArray(prev.categories) ? prev.categories : [];
      const exists = current.includes(categoryId);
      return {
        ...prev,
        categories: exists
          ? current.filter((id) => id !== categoryId)
          : [...current, categoryId],
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("loading");
    setMessage("");

    try {
      let featuredImageAssetId = "";
      if (form.featuredImage) {
        const uploadResponse = await fetch("/.netlify/functions/uploadAsset", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token?.access_token || ""}`,
          },
          body: JSON.stringify({
            fileName: form.featuredImage.name,
            contentType: form.featuredImage.type,
            base64Data: form.featuredImage.base64,
          }),
        });

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text();
          throw new Error(errorText || "Image upload failed.");
        }

        const uploadData = await uploadResponse.json();
        featuredImageAssetId = uploadData.assetId;
      }

      const contentMarkdown = turndown.turndown(editorValue || "");

      const endpoint = editSlug
        ? "/.netlify/functions/updatePost"
        : "/.netlify/functions/createPost";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token?.access_token || ""}`,
        },
        body: JSON.stringify({
          title: form.title,
          slug: form.slug,
          originalSlug: editSlug || undefined,
          shortDescription: form.shortDescription,
          location: form.location,
          publishedDate: form.publishedDate,
          contentMarkdown,
          featuredImageAssetId,
          categoryIds: form.categories,
          publishNow: form.publishNow,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Post creation failed.");
      }

      setStatus("success");
      setMessage(editSlug ? "Post updated." : "Post created.");
      setForm({
        title: "",
        slug: "",
        shortDescription: "",
        location: "",
        publishedDate: "",
        contentHtml: "",
        featuredImage: null,
        categories: [],
        publishNow: false,
      });
      setEditorValue("");
      setEditSlug("");
    } catch (error) {
      console.error(error);
      setStatus("error");
      setMessage(error.message || "Something went wrong.");
    }
  };

  const uploadImageAsset = useCallback(async (file) => {
    const reader = new FileReader();
    const base64 = await new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const uploadResponse = await fetch("/.netlify/functions/uploadAsset", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.token?.access_token || ""}`,
      },
      body: JSON.stringify({
        fileName: file.name,
        contentType: file.type,
        base64Data: base64,
      }),
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(errorText || "Image upload failed.");
    }

    const uploadData = await uploadResponse.json();
    const url = uploadData.url || "";
    return {
      assetId: uploadData.assetId,
      url: url.startsWith("//") ? `https:${url}` : url,
    };
  }, [user]);

  const handleInsertRichImage = async () => {
    richImageInputRef.current?.click();
  };

  const handleInsertCodeSnippet = async () => {
    const editor = editorRef.current;
    if (!editor) return;

    let selectedLanguage = "javascript";
    let codeValue = "";

    editor.windowManager.open({
      title: "Insert Code Snippet",
      body: {
        type: "panel",
        items: [
          {
            type: "selectbox",
            name: "language",
            label: "Language",
            items: [
              { text: "JavaScript", value: "javascript" },
              { text: "TypeScript", value: "typescript" },
              { text: "HTML", value: "html" },
              { text: "CSS", value: "css" },
              { text: "JSON", value: "json" },
              { text: "Bash", value: "bash" },
              { text: "Python", value: "python" },
              { text: "C", value: "c" },
              { text: "C++", value: "cpp" },
              { text: "Java", value: "java" },
            ],
          },
          {
            type: "textarea",
            name: "code",
            label: "Code",
            multiline: true,
            minHeight: 200,
          },
        ],
      },
      buttons: [
        { type: "cancel", text: "Cancel" },
        { type: "submit", text: "Insert", primary: true },
      ],
      initialData: {
        language: selectedLanguage,
        code: codeValue,
      },
      onSubmit: async (api) => {
        const data = api.getData();
        selectedLanguage = data.language || "javascript";
        codeValue = data.code || "";

        if (!codeValue.trim()) {
          api.close();
          return;
        }

        try {
          setStatus("loading");
          const response = await fetch("/.netlify/functions/createCodeSnippet", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token?.access_token || ""}`,
            },
            body: JSON.stringify({
              language: selectedLanguage,
              code: codeValue,
            }),
          });
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || "Code snippet creation failed.");
          }
          const result = await response.json();
          const entryId = result.entryId;
          if (!entryId) throw new Error("Missing code snippet id.");

          const safeLang = selectedLanguage.replace(/</g, "&lt;");
          const safeCode = codeValue.replace(/</g, "&lt;");
          const html = `<pre class="code-snippet-block" data-code-snippet-id="${entryId}"><div class="code-snippet-meta">${safeLang}</div><code>${safeCode}</code></pre>`;
          editor.insertContent(html);
          api.close();
        } catch (error) {
          console.error(error);
          setMessage(error.message || "Code snippet insert failed.");
          setStatus("error");
          api.close();
        } finally {
          setStatus("idle");
        }
      },
    });
  };
  const handleRichImageFileChange = async (event) => {
    const selectedFile = event.target.files && event.target.files[0];
    if (!selectedFile) return;

    const caption = window.prompt("Caption (optional)");
    const fullWidth = window.confirm("Make image full width?");

    try {
      setStatus("loading");
      const { assetId, url } = await uploadImageAsset(selectedFile);
      if (!assetId) throw new Error("Asset upload failed.");

      const createEntryResponse = await fetch(
        "/.netlify/functions/createRichImage",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token?.access_token || ""}`,
          },
          body: JSON.stringify({
            assetId,
            caption,
            fullWidth,
          }),
        }
      );

      if (!createEntryResponse.ok) {
        const errorText = await createEntryResponse.text();
        throw new Error(errorText || "Rich image entry failed.");
      }

      const createEntryData = await createEntryResponse.json();
      const entryId = createEntryData.entryId;
      if (!entryId) throw new Error("Missing rich image entry id.");

      const editor = editorRef.current;
      if (editor) {
        const safeCaption = caption ? caption.replace(/</g, "&lt;") : "";
        const figureHtml = `<figure class="blog-rich-image${
          fullWidth ? " full-width" : ""
        }"><img src="${url}" data-rich-image-id="${entryId}" alt="${
          safeCaption || "Rich image"
        }" />${
          safeCaption ? `<figcaption>${safeCaption}</figcaption>` : ""
        }</figure>`;
        editor.insertContent(figureHtml);
      }

    } catch (error) {
      console.error(error);
      setMessage(error.message || "Rich image insert failed.");
      setStatus("error");
    } finally {
      setStatus("idle");
      if (richImageInputRef.current) richImageInputRef.current.value = "";
    }
  };

  const editorInit = useMemo(
    () => ({
      height: 800,
      width: "100%",
      menubar: false,
      plugins: ["link", "lists", "code"],
      toolbar:
        "undo redo | styles | bold italic underline | bullist numlist | link hrline richimage codesnippet | code",
      block_formats:
        "Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6",
      content_style:
        "pre.code-snippet-block{background:#f5f5f5;border:1px solid #ddd;border-radius:4px;padding:10px;} pre.code-snippet-block code{font-family:Menlo,Consolas,Monaco,monospace;} .code-snippet-meta{font-size:0.75rem;letter-spacing:.06em;text-transform:uppercase;color:#666;margin-bottom:6px;} .blog-rich-image{max-width:100%;margin:1rem 0;} .blog-rich-image img{max-width:100%;height:auto;display:block;}",
      min_height: 600,
      images_upload_handler: async (blobInfo) => {
        const file = blobInfo.blob();
        const { url } = await uploadImageAsset(file);
        return url;
      },
      file_picker_types: "image",
      file_picker_callback: (callback, _value, meta) => {
        if (meta.filetype !== "image") return;
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.onchange = async () => {
          const file = input.files && input.files[0];
          if (!file) return;
          try {
            const { url } = await uploadImageAsset(file);
            callback(url, { alt: file.name });
          } catch (error) {
            console.error(error);
            setMessage(error.message || "Image upload failed.");
          }
        };
        input.click();
      },
      setup: (editor) => {
        editor.ui.registry.addButton("richimage", {
          text: "Rich Image",
          onAction: () => handleInsertRichImage(),
        });
        editor.ui.registry.addButton("codesnippet", {
          text: "Code Snippet",
          onAction: () => handleInsertCodeSnippet(),
        });
        editor.ui.registry.addButton("hrline", {
          tooltip: "Horizontal line",
          icon: "horizontal-rule",
          onAction: () => editor.insertContent("<hr />"),
        });
      },
    }),
    [uploadImageAsset, handleInsertRichImage, handleInsertCodeSnippet]
  );

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      updateField("featuredImage", {
        name: file.name,
        type: file.type,
        base64,
      });
    };
    reader.readAsDataURL(file);
  };

  if (!user) {
    return (
      <section className="blog-section">
        <article className="blog-card admin-card">
          <h1>Admin</h1>
          <p>Sign in to manage posts.</p>
          <button
            type="button"
            className="contact-btn"
            onClick={() => netlifyIdentity.open("login")}
          >
            Sign in
          </button>
        </article>
      </section>
    );
  }

  return (
    <section className="blog-section">
      <article className="blog-card admin-card">
        <div className="admin-header">
          <h1>{editSlug ? "Edit Blog Post" : "New Blog Post"}</h1>
          <button
            type="button"
            className="contact-btn"
            onClick={() => netlifyIdentity.logout()}
          >
            Sign out
          </button>
        </div>

        <form onSubmit={handleSubmit} className="admin-form">
          <label>
            Title
            <input
              type="text"
              value={form.title}
              onChange={(event) => updateField("title", event.target.value)}
              required
            />
          </label>

          <label>
            Slug
            <input
              type="text"
              value={form.slug}
              onChange={(event) => updateField("slug", event.target.value)}
              required
            />
          </label>

          <label>
            Subtitle
            <input
              type="text"
              value={form.shortDescription}
              onChange={(event) =>
                updateField("shortDescription", event.target.value)
              }
            />
          </label>

          <label>
            Location
            <input
              type="text"
              value={form.location}
              onChange={(event) => updateField("location", event.target.value)}
              placeholder="Kamakura, Kanagawa"
            />
          </label>

          {availableCategories.length > 0 && (
            <fieldset className="admin-categories">
              <legend>Categories</legend>
              <div className="admin-category-list">
                {availableCategories.map((category) => (
                  <label
                    className="admin-checkbox admin-category"
                    key={category.id}
                    title={category.label}
                  >
                    <input
                      type="checkbox"
                      checked={form.categories.includes(category.id)}
                      onChange={() => toggleCategory(category.id)}
                    />
                    <span>{category.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          <label>
            Published date
            <input
              type="date"
              value={form.publishedDate}
              onChange={(event) =>
                updateField("publishedDate", event.target.value)
              }
              required
            />
          </label>

          <label>
            Content
            <Editor
              apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
              onInit={(_, editor) => {
                editorRef.current = editor;
              }}
              value={editorValue}
              onEditorChange={(value) => setEditorValue(value)}
              init={editorInit}
            />
          </label>
          <input
            ref={richImageInputRef}
            type="file"
            accept="image/*"
            onChange={handleRichImageFileChange}
            style={{ display: "none" }}
          />
          <label>
            Featured image
            <input type="file" accept="image/*" onChange={handleFileChange} />
          </label>

          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.publishNow}
              onChange={(event) =>
                updateField("publishNow", event.target.checked)
              }
            />
            <span>Publish immediately</span>
          </label>

          <button type="submit" className="contact-btn" disabled={status === "loading"}>
            {status === "loading" ? "Saving..." : "Create post"}
          </button>

          {message && <p className="admin-message">{message}</p>}
        </form>
      </article>
    </section>
  );
};

export default Admin;
